import { formatDateString } from '@flecto/utils'
import { getDayIndex } from '@flecto/utils/src/Date/localizeDayName'
import { DayOfWeek } from '@flecto/utils/src/Date/ScheduleHours'

import { type OperatingSchedule } from '../../../types/OperatingSchedule'
export class RangeHour {
  key = ''
  constructor(
    public start: Date,
    public end: Date,
    key?: string
  ) {
    this.key = key || Math.random().toString(36)
  }
}

export const getDefaultTime = () =>
  new RangeHour(
    new Date(2023, 1, 1, 9, 0, 0, 0),
    new Date(2023, 1, 1, 18, 0, 0, 0)
  )

export class RangeEmitterData {
  private data: string[][] = []
  constructor(
    private hours: RangeHour[],
    private day: keyof typeof DayOfWeek | DayOfWeek
  ) {
    this.normalize()
  }

  normalize() {
    this.data = this.hours.map((hour) => {
      const start = new Date(hour.start)
      const end = new Date(hour.end)

      const formattedStart = formatDateString({
        date: start.toString(),
        variant: 'time-brief',
      })

      const formattedEnd = formatDateString({
        date: end.toString(),
        variant: 'time-brief',
      })

      return [
        `2000-01-01T${formattedStart}:00`,
        `2000-01-01T${formattedEnd}:00`,
      ]
    })
  }

  private get dayOfWeek() {
    return getDayIndex(this.day)
  }
  get payload() {
    return {
      [this.dayOfWeek]: this.data,
    }
  }
}

export class RangeEmitterDataBuilder {
  public data: OperatingSchedule = {}

  constructor() {
    const daysOfWeek = [
      DayOfWeek.Monday,
      DayOfWeek.Tuesday,
      DayOfWeek.Wednesday,
      DayOfWeek.Thursday,
      DayOfWeek.Friday,
      DayOfWeek.Saturday,
      DayOfWeek.Sunday,
    ]

    for (const day of daysOfWeek) {
      this.data = {
        ...this.data,
        ...new RangeEmitterData([getDefaultTime()], day).payload,
      }
    }
  }
}

export class HourValidation {
  constructor(private hours: RangeHour[]) {
    this.normalize()
  }

  normalize() {
    // set all dates to the same day
    this.hours = this.hours.map((hour) => {
      const start = new Date(hour.start)
      const end = new Date(hour.end)
      start.setFullYear(2000, 0, 1)
      end.setFullYear(2000, 0, 1)
      return new RangeHour(start, end, hour.key)
    })
  }

  validate() {
    const sortedHours = this.hours.sort(
      (a, b) => a.start.getTime() - b.start.getTime()
    )
    const overlappingHours = []
    const duplicateHours = []
    const invalidHours = []
    for (let i = 0; i <= sortedHours.length - 1; i++) {
      const current = sortedHours[i]
      const next = sortedHours[i + 1]

      // Validate invalid hours
      if (current.start.getTime() > current.end.getTime()) {
        invalidHours.push(current)
      }

      if (!next) {
        continue
      }

      // Validate overlapping hours
      if (current.end.getTime() > next.start.getTime()) {
        overlappingHours.push(current, next)
      }
      if (
        current.start.getTime() === next.start.getTime() &&
        current.end.getTime() === next.end.getTime()
      ) {
        duplicateHours.push(current, next)
      }
    }

    return {
      invalidHoursKeys: invalidHours.map((hour) => hour.key),
      overlappingHoursKeys: overlappingHours.map((hour) => hour.key),
      duplicateHoursKeys: duplicateHours.map((hour) => hour.key),
    }
  }
}
